import { Skeleton } from '@mui/material';
import React from 'react';

import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { removeAppBuilderPlaceholder } from 'src/store/appBuilderPlaceholder/slice';

// Temporary, skeleton-only sidebar rows shown while an app builder creation is
// in flight. The backend creates the real install (with its name + icon) during
// prepare-build, so each placeholder is dropped as soon as that install lands in
// getInstalls for the same appBuilderId.
export const AppBuilderPlaceholderSidebarItems = () => {
  const dispatch = useAppDispatch();
  const isClient = useAppSelector((state) => state.user.isClient);
  const placeholders = useAppSelector(
    (state) => state.appBuilderPlaceholder.placeholders,
  );
  const { data } = useGetInstallsQuery();

  const installedAppBuilderIds = React.useMemo(() => {
    const ids = new Set<string>();
    Object.values(data?.extensionsSettings ?? {}).forEach((extensionConfig) => {
      if (extensionConfig.appBuilderId) ids.add(extensionConfig.appBuilderId);
    });
    return ids;
  }, [data?.extensionsSettings]);

  React.useEffect(() => {
    placeholders.forEach((placeholder) => {
      if (
        placeholder.appBuilderId &&
        installedAppBuilderIds.has(placeholder.appBuilderId)
      ) {
        dispatch(removeAppBuilderPlaceholder({ id: placeholder.id }));
      }
    });
  }, [placeholders, installedAppBuilderIds, dispatch]);

  if (isClient) return null;

  const pendingPlaceholders = placeholders.filter(
    (placeholder) =>
      !placeholder.appBuilderId ||
      !installedAppBuilderIds.has(placeholder.appBuilderId),
  );

  if (!pendingPlaceholders.length) return null;

  return (
    <>
      {pendingPlaceholders.map((placeholder) => (
        <div
          key={placeholder.id}
          data-testid={`sidebar-app-placeholder-${placeholder.id}`}
          className="mx-2 my-1 flex h-7 items-center px-2"
        >
          <div className="flex min-w-6 items-center">
            <Skeleton variant="circular" width={14} height={14} />
          </div>
          <Skeleton
            variant="rectangular"
            width="55%"
            height={12}
            className="rounded"
          />
        </div>
      ))}
    </>
  );
};
